import React, { FC } from 'react';
import Loadable from 'react-loadable';
import { CartPageSkeleton } from 'b2b-apps/core/cart/components/CartPage/CartPageSkeleton';
import { config } from 'b2b-apps/shop/config';
import { ModularPageProductsGroupContextProvider } from 'b2b-apps/shop/content/components/ModularPage/ModularPage/Context/ModularPageProductsGroupContext';
import { PERMISSION } from 'b2b-apps/shop/context/utils/permissions';
import { HomePage } from 'b2b-apps/shop/home/components/HomePage';
import { PageLoader } from 'b2b-apps/shop/routes/components/PageLoader';
import { getLocalisedPaths } from 'b2b-apps/shop/routes/utils/paths';
import { AccessCallbackDataType, RouteType } from './';

enum ROUTES_NAMES {
  homepage = 'homepage',
  cart = 'cart',
  checkout = 'checkout',
  checkoutConfirmation = 'checkoutConfirmation',
  checkoutFail = 'checkoutFail',
  category = 'category',
  search = 'search',
  brand = 'brand',
  manufacturer = 'manufacturer',
  manufacturers = 'manufacturers',
  product = 'product',
  page = 'page',
  collectedOrderConfirmation = 'collectedOrderConfirmation',
  collectedOrderFail = 'collectedOrderFail',
  myAccount = 'myAccount',
  myAccountBalanceDetails = 'myAccountBalanceDetails',
  myAccountInvoices = 'myAccountInvoices',
  myAccountInvoice = 'myAccountInvoice',
  myAccountCredits = 'myAccountCredits',
  myAccountOrders = 'myAccountOrders',
  myAccountOrder = 'myAccountOrder',
  myAccountDeliveredOrders = 'myAccountDeliveredOrders',
  myAccountDetails = 'myAccountDetails',
  myAccountBillingAddressUpdate = 'myAccountBillingAddressUpdate',
  myAccountContactInfoUpdate = 'myAccountContactInfoUpdate',
  myAccountAddresses = 'myAccountAddresses',
  myAccountAddressCreate = 'myAccountAddressCreate',
  myAccountAddressUpdate = 'myAccountAddressUpdate',
  myAccountUsers = 'myAccountUsers',
  myAccountUserCreate = 'myAccountUserCreate',
  myAccountUserUpdate = 'myAccountUserUpdate',
  myAccountWishlists = 'myAccountWishlists',
  myAccountWishlist = 'myAccountWishlist',
  wishlist = 'wishlist',
  myAccountRmasSearch = 'myAccountRmasSearch',
  rmasSearch = 'rmasSearch',
  myAccountRmas = 'myAccountRmas',
  myAccountCreateRma = 'myAccountCreateRma',
  createRma = 'createRma',
  myAccountRma = 'myAccountRma',
  myAccountRmaConfirmation = 'myAccountRmaConfirmation',
  rmaConfirmation = 'rmaConfirmation',
  myAccountUpdateRma = 'myAccountUpdateRma',
  myAccountNewsletters = 'myAccountNewsletters',
  expressOrder = 'expressOrder',
  newsletterConfirmation = 'newsletterConfirmation',
  newsletterSignUpFail = 'newsletterSignUpFail',
  newsletterSignUpSuccess = 'newsletterSignUpSuccess',
  punchoutLoginUnsuccessful = 'punchoutLoginUnsuccessful',
  punchoutOrderConfirmation = 'punchoutOrderConfirmation',
  ociTestForm = 'ociTestForm',
  ociTestResult = 'ociTestResult',
  cxmlTestForm = 'cxmlTestForm',
  cxmlTestResult = 'cxmlTestResult',
  productsCompare = 'productsCompare',
  trackingOptOut = 'trackingOptOut',
  kanban = 'kanban',
  kanbanOrderConfirmation = 'kanbanOrderConfirmation',
  kanbanOrderFail = 'kanbanOrderFail'
}

const DEFAULT_ROUTE: RouteType = {
  paths: {},
  allow: () => true,
  allowTranslation: true,
};

const DEFAULT_ROUTE_PARAMS: Record<string, any> = {
  step: 1,
  page: 1,
};

const ROUTES: Record<ROUTES_NAMES, RouteType> = Object.freeze({
  [ROUTES_NAMES.homepage]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('', true),
    exact: true,
    render: function renderHomePage() {
      const Component = HomePage as FC;

      return (
        <ModularPageProductsGroupContextProvider>
          <Component />
        </ModularPageProductsGroupContextProvider>
      );
    },
  },
  [ROUTES_NAMES.cart]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('cart', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/cart/components/CartPage'
        /* webpackChunkName: "cart" */
      ),
      loading: CartPageSkeleton,
      modules: ['b2b-apps/shop/cart/components/CartPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      isBlocked,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && !isBlocked && permissionsValidator([PERMISSION.accessCart]),
  },
  [ROUTES_NAMES.category]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('category'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/catalog/components/CategoryPage'
        /* webpackChunkName: "category" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/catalog/components/CategoryPage'],
    }) as FC,
    allowTranslation: false,
  },
  [ROUTES_NAMES.search]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('search'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/catalog/components/SearchPage'
        /* webpackChunkName: "search" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/catalog/components/SearchPage'],
    }) as FC,
    allowTranslation: false,
  },
  [ROUTES_NAMES.brand]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('brand'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/catalog/components/BrandPage'
        /* webpackChunkName: "brand" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/catalog/components/BrandPage'],
    }) as FC,
    allowTranslation: false,
  },
  [ROUTES_NAMES.checkout]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('checkout/:step?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/checkout/components/CheckoutPage'
        /* webpackChunkName: "checkout" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/checkout/components/CheckoutPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      isBlocked,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && !isBlocked
      && permissionsValidator([PERMISSION.accessCheckout]),
  },
  [ROUTES_NAMES.checkoutConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('checkout-confirmation'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/checkout/components/ConfirmationPage'
        /* webpackChunkName: "checkoutConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/checkout/components/ConfirmationPage'],
    }) as FC,
  },
  [ROUTES_NAMES.checkoutFail]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('checkout-confirmation/fail'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/checkout/components/FailPage'
        /* webpackChunkName: "checkoutFail" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/checkout/components/FailPage'],
    }) as FC,
  },
  [ROUTES_NAMES.collectedOrderConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('collected-order/confirmation'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/collectedOrder/components/ConfirmationPage'
        /* webpackChunkName: "collectedOrderConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/collectedOrder/components/ConfirmationPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      isBlocked,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && !isBlocked
      && permissionsValidator([PERMISSION.accessCheckoutCollectOrder]),
  },
  [ROUTES_NAMES.collectedOrderFail]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('collected-order/fail'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/collectedOrder/components/FailPage'
        /* webpackChunkName: "collectedOrderFail" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/collectedOrder/components/FailPage'],
    }) as FC,
  },
  [ROUTES_NAMES.punchoutLoginUnsuccessful]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('punchout'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/punchout/common/components/AuthFailPage'
        /* webpackChunkName: "punchoutLoginUnsuccessfulPage" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/common/components/AuthFailPage'],
    }) as FC,
  },
  [ROUTES_NAMES.punchoutOrderConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('punchout/confirmation/:punchoutType'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/punchout/common/components/ConfirmationPage'
        /* webpackChunkName: "punchoutOrderConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/common/components/ConfirmationPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      isBlocked,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && !isBlocked
      && (permissionsValidator([PERMISSION.oci])
        || permissionsValidator([PERMISSION.cxml])),
  },
  [ROUTES_NAMES.product]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('product/:sku', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/product/components/ProductPage'
        /* webpackChunkName: "product" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/product/components/ProductPage'],
    }) as FC,
  },
  [ROUTES_NAMES.page]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('pages/:entryId(.+)', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/content/components/ContentPage'
        /* webpackChunkName: "content" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/content/components/ContentPage'],
    }) as FC,
  },
  [ROUTES_NAMES.myAccount]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/dashboard'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/dashboard/components/DashboardPage'
        /* webpackChunkName: "myAccountDashboard" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/dashboard/components/DashboardPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountDashboard]),
  },
  [ROUTES_NAMES.myAccountBalanceDetails]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/balance-details'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/balanceDetails/components/ViewPage'
        /* webpackChunkName: "myAccountBalanceDetails" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/balanceDetails/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountInvoices]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/invoices/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/invoices/components/ListPage'
        /* webpackChunkName: "myAccountInvoices" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/invoices/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountInvoice]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/invoice/:invoiceId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/invoices/components/ViewPage'
        /* webpackChunkName: "myAccountInvoice" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/invoices/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountCredits]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/credits/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/credits/components/ListPage'
        /* webpackChunkName: "myAccountCredits" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/credits/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountOrders]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/orders/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/orders/components/ListPage'
        /* webpackChunkName: "myAccountOrders" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/orders/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountOrder]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/order/:orderId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/orders/components/ViewPage'
        /* webpackChunkName: "myAccountOrder" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/orders/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountDeliveredOrders]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/delivered-orders/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/deliveredOrders/components/ListPage'
        /* webpackChunkName: "myAccountDeliveredOrders" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/deliveredOrders/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountDetails]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/details'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/accountDetails/components/ViewPage'
        /* webpackChunkName: "myAccountDetails" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/accountDetails/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountBillingAddressUpdate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/details/update-billing-address'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/accountDetails/components/UpdateBillingAddressPage'
        /* webpackChunkName: "myAccountBillingAddressUpdate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/accountDetails/components/UpdateBillingAddressPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && permissionsValidator([
        PERMISSION.accessMyAccountUserBillingAddressChange,
      ]),
  },
  [ROUTES_NAMES.myAccountContactInfoUpdate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/details/update-contact-information'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/accountDetails/components/UpdateContactInformationPage'
        /* webpackChunkName: "myAccountContactInfoUpdate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/accountDetails/components/UpdateContactInformationPag'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && permissionsValidator([
        PERMISSION.accessMyAccountUserContactInformationChange,
      ]),
  },
  [ROUTES_NAMES.myAccountAddresses]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/addresses/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/addresses/components/ListPage'
        /* webpackChunkName: "myAccountAddresses" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/addresses/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountAddresses]),
  },
  [ROUTES_NAMES.myAccountAddressCreate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/address/create'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/addresses/components/CreatePage'
        /* webpackChunkName: "myAccountAddressCreate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/addresses/components/CreatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountAddressUpdate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/address/update/:id'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/addresses/components/UpdatePage'
        /* webpackChunkName: "myAccountAddressUpdate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/addresses/components/UpdatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountBasic]),
  },
  [ROUTES_NAMES.myAccountUsers]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/users/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/users/components/ListPage'
        /* webpackChunkName: "myAccountUsers" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/users/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn
      && permissionsValidator([PERMISSION.accessMyAccountUserManagement]),
  },
  [ROUTES_NAMES.myAccountUserCreate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/user/create'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/users/components/CreatePage'
        /* webpackChunkName: "myAccountUserCreate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/users/components/CreatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.myAccountEditUser]),
  },
  [ROUTES_NAMES.myAccountUserUpdate]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/user/update/:accountId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/users/components/UpdatePage'
        /* webpackChunkName: "myAccountUserUpdate" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/users/components/UpdatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.myAccountEditUser]),
  },
  [ROUTES_NAMES.myAccountWishlists]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/wishlists/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/wishlists/components/ListPage'
        /* webpackChunkName: "myAccountWishlists" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/wishlists/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessWishlist]),
  },
  [ROUTES_NAMES.myAccountWishlist]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/wishlist/:wishlistId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/wishlists/components/ViewPage'
        /* webpackChunkName: "myAccountWishlist" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/wishlists/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessWishlist]),
  },
  [ROUTES_NAMES.wishlist]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('wishlist/:wishlistId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/wishlists/components/StandaloneViewPage'
        /* webpackChunkName: "wishlist" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/wishlists/components/StandaloneViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessWishlist]),
  },
  [ROUTES_NAMES.myAccountRmasSearch]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rmas/search'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/SearchPage'
        /* webpackChunkName: "myAccountRmasSearch" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/SearchPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.rmasSearch]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('rmas/search', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/StandaloneSearchPage'
        /* webpackChunkName: "rmasSearch" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/StandaloneSearchPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => !isLoggedIn
      || (isLoggedIn && !permissionsValidator([PERMISSION.accessMyAccountRma])),
  },
  [ROUTES_NAMES.myAccountRmas]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rmas/:page?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/ListPage'
        /* webpackChunkName: "myAccountRmas" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/ListPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.myAccountCreateRma]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rma/create/:type/:invoiceId/:sku?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/CreatePage'
        /* webpackChunkName: "myAccountCreateRma" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/CreatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.createRma]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('rma/create/:type/:debitorId/:invoiceId/:sku?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/StandaloneCreatePage'
        /* webpackChunkName: "createRma" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/StandaloneCreatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => !isLoggedIn
      || (isLoggedIn && !permissionsValidator([PERMISSION.accessMyAccountRma])),
  },
  [ROUTES_NAMES.myAccountRma]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rma/:rmaId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/ViewPage'
        /* webpackChunkName: "myAccountRma" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.myAccountRmaConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rma/:rmaId/confirmation'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/ConfirmationPage'
        /* webpackChunkName: "myAccountRmaConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/ConfirmationPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.rmaConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('rma/confirmation/:debitorId/:rmaId'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/StandaloneConfirmationPage'
        /* webpackChunkName: "rmaConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/StandaloneConfirmationPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => !isLoggedIn
      || (isLoggedIn && !permissionsValidator([PERMISSION.accessMyAccountRma])),
  },
  [ROUTES_NAMES.myAccountUpdateRma]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/rma/:rmaId/update'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/rma/components/UpdatePage'
        /* webpackChunkName: "myAccountUpdateRma" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/rma/components/UpdatePage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessMyAccountRma]),
  },
  [ROUTES_NAMES.myAccountNewsletters]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('my-account/newsletters'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/myAccount/newsletters/components/ViewPage'
        /* webpackChunkName: "myAccountNewsletters" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/myAccount/newsletters/components/ViewPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => !!config.features.newsletter
      && isLoggedIn
      && permissionsValidator([PERMISSION.accessMyAccountNewsletters]),
  },
  [ROUTES_NAMES.manufacturer]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('manufacturer/:manufacturer'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/manufacturer/components/ViewPage'
        /* webpackChunkName: "manufacturerViewPage" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/manufacturer/components/ViewPage'],
    }) as FC,
  },
  [ROUTES_NAMES.manufacturers]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('manufacturers'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/manufacturer/components/ListPage'
        /* webpackChunkName: "manufacturerListPage" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/manufacturer/components/ListPage'],
    }) as FC,
  },
  [ROUTES_NAMES.expressOrder]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('express-order'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/expressOrder/components/ExpressOrderPage'
        /* webpackChunkName: "expressOrder" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/expressOrder/components/ExpressOrderPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => !!config.features.expressOrder
      && isLoggedIn
      && (permissionsValidator([PERMISSION.accessCart])
        || permissionsValidator([PERMISSION.accessWishlist])),
  },
  [ROUTES_NAMES.newsletterConfirmation]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('newsletter/confirmation', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/newsletter/components/NewsletterConfirmationPage'
        /* webpackChunkName: "newsletterConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/newsletter/components/NewsletterConfirmationPage'],
    }) as FC,
    allow: (): boolean => !!config.features.newsletter,
  },
  [ROUTES_NAMES.newsletterSignUpFail]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('newsletter/signup/fail', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/newsletter/components/NewsletterFailedPage'
        /* webpackChunkName: "newsletterFailed" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/newsletter/components/NewsletterFailedPage'],
    }) as FC,
    allow: (): boolean => !!config.features.newsletter,
  },
  [ROUTES_NAMES.newsletterSignUpSuccess]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('newsletter/signup/success', true),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/newsletter/components/NewsletterSuccessfulPage'
        /* webpackChunkName: "newsletterSuccessful" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/newsletter/components/NewsletterSuccessfulPage'],
    }) as FC,
    allow: (): boolean => !!config.features.newsletter,
  },
  [ROUTES_NAMES.productsCompare]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('products/compare/:skus?'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import(
        'b2b-apps/shop/product/components/ProductsComparePage'
        /* webpackChunkName: "productsComparePage" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/product/components/ProductsComparePage'],
    }) as FC,
  },
  [ROUTES_NAMES.ociTestForm]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('test/oci-form'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/punchout/testPages/components/OciTestFormPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/testPages/components/OciTestFormPage'],
    }) as FC,
  },
  [ROUTES_NAMES.ociTestResult]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('test/oci-result'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/punchout/testPages/components/OciTestResultPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/testPages/components/OciTestResultPage'],
    }) as FC,
  },
  [ROUTES_NAMES.cxmlTestForm]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('test/cxml-form'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/punchout/testPages/components/CXMLTestFormPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/testPages/components/CXMLTestFormPage'],
    }) as FC,
  },
  [ROUTES_NAMES.cxmlTestResult]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('test/cxml-result'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/punchout/testPages/components/CXMLTestResultPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/punchout/testPages/components/CXMLTestResultPage'],
    }) as FC,
  },
  [ROUTES_NAMES.trackingOptOut]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('tracking-opt-out'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/settings/components/TrackingOptOutPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/settings/components/TrackingOptOutPage'],
    }) as FC,
  },
  [ROUTES_NAMES.kanban]: {
    ...DEFAULT_ROUTE,
    paths: getLocalisedPaths('kanban'),
    component: Loadable<Record<string, any>, any>({
      loader: () => import('b2b-apps/shop/kanban/components/KanbanPage'),
      loading: PageLoader,
      modules: ['b2b-apps/shop/kanban/components/KanbanPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessKanban]),
  },
  [ROUTES_NAMES.kanbanOrderConfirmation]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('kanban-order/confirmation'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/kanban/components/ConfirmationPage'
        /* webpackChunkName: "kanbanOrderConfirmation" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/kanban/components/ConfirmationPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessKanban]),
  },
  [ROUTES_NAMES.kanbanOrderFail]: {
    ...DEFAULT_ROUTE,
    exact: true,
    paths: getLocalisedPaths('kanban-order/fail'),
    component: Loadable<any, any>({
      loader: () => import(
        'b2b-apps/shop/kanban/components/FailPage'
        /* webpackChunkName: "kanbanOrderFail" */
      ),
      loading: PageLoader,
      modules: ['b2b-apps/shop/kanban/components/FailPage'],
    }) as FC,
    allow: ({
      isLoggedIn,
      permissionsValidator,
    }: AccessCallbackDataType): boolean => isLoggedIn && permissionsValidator([PERMISSION.accessKanban]),
  },
});

enum SERVER_ROUTES {
  login = '/auth/login',
  logout = '/auth/logout',
  register = '/auth/register',
  passwordChange = '/auth/change-password',
}

export {
  DEFAULT_ROUTE,
  DEFAULT_ROUTE_PARAMS,
  ROUTES,
  SERVER_ROUTES,
};
